import type { MouseEvent } from 'react';
import styled, { css } from 'styled-components';
import { ColorNeutralWhite, Spacing4, Spacing12, Spacing16, Spacing24, Spacing32 } from 'autogen/design-tokens/tokens';
import type { MenuHeaderViewModel, MenuViewModel } from 'autogen/swagger/all';
import { ButtonWithIcon } from 'components/Button';
import { closeAllDrawers } from 'components/generic/Drawer/hooks/useDrawer';
import { Typography } from 'components/generic/typography';
import Icon from 'components/Icon';
import Logo from 'components/Icon/Logo';
import LinkAction from 'components/shared/Routing/components/LinkAction';
import useIsAuthenticated from 'hooks/user/useIsAuthenticated';
import usePointOfSaleRequest from 'hooks/user/usePointOfSaleRequest';
import { GA4Tracking } from 'modules/tracking/GA4';
import { breakpoint, sizes } from 'variables';

const MenuHeaderContainer = styled.div<{ styling?: MenuViewModel['styling'] }>`
    position: sticky;
    top: 0;
    display: flex;
    gap: ${Spacing24};
    padding: ${Spacing24} ${Spacing16};
    background-color: ${({ styling, theme }) => {
        switch (styling) {
            case 'White':
                return ColorNeutralWhite;
            default:
                return theme.ColorBackgroundSecondary;
        }
    }};

    ${breakpoint.up(sizes.md)} {
        margin-bottom: 0;
        padding: ${Spacing32} ${Spacing16};
    }
`;

const HeadlineContainerStyling = css`
    padding: 0 ${Spacing16};
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
`;

const LogoContainer = styled.div`
    flex-grow: 0;
    height: 24px;

    svg {
        height: inherit;
    }

    ${breakpoint.up(sizes.md)} {
        height: 32px;
    }
`;

const Headline = styled(Typography.Headline)`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const StyledButtonWithIcon = styled(ButtonWithIcon)`
    flex-grow: 0;

    &:hover,
    &:focus {
        background-color: transparent;
    }
`;

const Container = styled.div`
    flex-grow: 1;
    overflow: hidden;

    ${HeadlineContainerStyling}
`;

const CloseButton = styled(StyledButtonWithIcon)`
    flex-grow: 0;
    margin-left: auto;
`;

const HeaderLinksContainer = styled(Typography.Body)`
    display: flex;
    flex-direction: column; // the links are stacked on mobile due to long texts on the Finish website

    ${breakpoint.up('768px')} {
        align-self: flex-end;
        flex-direction: row;
        margin-bottom: ${Spacing4};

        a:not(:first-child) {
            border-left: 1px solid #000;
        }

        a:first-child {
            padding-right: ${Spacing12};
        }

        a:last-child {
            padding-left: ${Spacing12};
        } 
    }
`;

const LogoLink = styled.a`
    display: block;
    height: inherit;
`;

export type MenuHeaderProps = {
    backButtonCallback?: () => void;
    closeButtonCallback?: () => void;
    headline?: string;
    styling?: MenuViewModel['styling'];
};

export default function MenuHeader({
    backButtonCallback,
    closeButtonCallback,
    headline,
    styling,
    ...restProps
}: MenuHeaderProps) {
    return (
        <MenuHeaderContainer styling={styling} {...restProps}>
            {backButtonCallback && (
                <StyledButtonWithIcon
                    onClick={backButtonCallback}
                    icon={<Icon type="ArrowLeft" size={16} />}
                    size="md"
                    variant="ghostOnLight"
                />
            )}

            {headline && (
                <Container>
                    <Headline size="L">{headline}</Headline>
                </Container>
            )}

            <CloseButton
                onClick={closeButtonCallback || closeAllDrawers}
                icon={<Icon type="Close" size={16} />}
                size="md"
                variant="ghostOnLight"
            />
        </MenuHeaderContainer>
    );
}

const StyledMenuHeaderContainer = styled(MenuHeaderContainer)`
    display: flex;
    align-items: center;
    flex-direction: row;
`;

type MenuHeaderWithHeaderLinksProps = MenuHeaderProps & { headerLinks?: MenuViewModel['headerLinks'] };

export function MenuHeaderWithMatasLogo({
    backButtonCallback,
    closeButtonCallback,
    headline,
    headerLinks,
    styling
}: MenuHeaderWithHeaderLinksProps) {
    const isAuthenticated = useIsAuthenticated();
    const { isPosRequest } = usePointOfSaleRequest();

    function headerLinksTracking(event: MouseEvent<HTMLAnchorElement>) {
        const {
            currentTarget: { textContent }
        } = event;

        if (textContent) {
            GA4Tracking({
                eventName: 'link_click',
                context: 'menu',
                category: 'navigation',
                eventParams: [{ param_name: 'link_text', param_value: textContent }]
            });
        }
    }

    return (
        <StyledMenuHeaderContainer styling={styling}>
            {backButtonCallback && (
                <StyledButtonWithIcon
                    onClick={backButtonCallback}
                    icon={<Icon type="ArrowLeft" size={16} />}
                    size="md"
                    variant="ghostOnLight"
                />
            )}

            {headline && (
                <Container>
                    <Headline size="L">{headline}</Headline>
                </Container>
            )}
            {!headline && (
                <>
                    <LogoContainer>
                        <LogoLink href={isPosRequest ? undefined : '/'}>
                            <Logo />
                        </LogoLink>
                    </LogoContainer>
                    {headerLinks && !isPosRequest && (
                        <HeaderLinksContainer forwardedAs="div" size="S">
                            {headerLinks
                                .reduce(
                                    (acc, curr) => [...acc, isAuthenticated ? curr.loggedInLink : curr.loggedOutLink],
                                    [] as (MenuHeaderViewModel['loggedInLink'] | MenuHeaderViewModel['loggedOutLink'])[]
                                )
                                .map((menuHeaderLink) => {
                                    const { link, text } = menuHeaderLink;

                                    return (
                                        <LinkAction action={link.linkAction} key={text} onClick={headerLinksTracking}>
                                            {text}
                                        </LinkAction>
                                    );
                                })}
                        </HeaderLinksContainer>
                    )}
                </>
            )}

            <CloseButton
                onClick={closeButtonCallback || closeAllDrawers}
                icon={<Icon type="Close" size={16} />}
                size="md"
                variant="ghostOnLight"
            />
        </StyledMenuHeaderContainer>
    );
}
